<template>
    <section>
        <o-tabs type="toggle" v-model="activeTab" :animated="false">
            <o-tab-item :value="0" label="Editor">
                <h1 class="has-text-centered">Markdown Editor</h1>
                <MdFields 
                    :primaryImageSelected="primaryImageSelected"
                    :imageUrl="primaryImageFilename"
                    @mdFields="mdFields"
                    :updateFields="fields"
                    @choose-image="onChooseImage"
                />

                <div 
                    v-show="loading || showSavedMsg"
                    class="container has-text-centered mb-5"
                >
                    
                    <LoadingSpinner v-show="loading" class="m-3"/>
                    
                    <NotificationMessage
                        v-show="showSavedMsg"
                        class="p-5 m-2"
                        bulma-color-class="is-primary"
                        @delete-msg-clicked="showSavedMsg = false">
                        <p>Post saved to S3 successfully</p>
                    </NotificationMessage>

                </div>

                <MdEditor
                    v-model="text" 
                    language="en-US" 
                    :toolbarsExclude="[ 
                        'mermaid',
                        'prettier',
                        'htmlPreview',
                        'catalog',
                        'github' ]" 
                    @on-save="onSave"
                    @onUploadImg="onUploadImg"
                />

            </o-tab-item>
            <o-tab-item :value="1" label="Gallery">
                <Gallery 
                    @image-object="onImageObject" 
                    @primary-image-object="onPrimaryImageObject" 
                    @images-refreshed="refreshImages = false"
                    :refreshImages="refreshImages"
                />
            </o-tab-item>
        </o-tabs>
        <div class="has-text-right m-4">
            <button @click="onOpen" class="button is-light">
                <span class="icon"><i class="fa-solid fa-folder-open"></i></span>
                <span>Open existing post</span>
            </button>
        </div>
    </section>

    <ModalSimple 
        :active="saveModalActive"
        @closed="saveModalActive = false"
        title="Save Post"
    >
        <SaveButtons
            @save-fields="saveFieldsEvent"
        />
    </ModalSimple>

    <ModalSimple 
        :active="this.openModalActive"
        @closed="this.openModalActive = false"
        :title="'Open Post'"
    >
        <OpenButtons @post-opened="onPostOpened">

        </OpenButtons>
    </ModalSimple>
</template>

<script>
// components
import MdFields from '@/components/editor/MdFields.vue'
import Gallery from '@/components/editor/Gallery.vue'
import SaveButtons from '@/components/editor/SaveButtons.vue'
import OpenButtons from '@/components/editor/OpenButtons.vue'
//widgets
import ModalSimple from '@/widgets/ModalSimple.vue'
import LoadingSpinner from '@/widgets/LoadingSpinner.vue'
import NotificationMessage from '@/widgets/NotificationMessage.vue'
//js
import { removeFileExtension, saveTextFileLocally } from '@/js/general'
import { putTextFile, addFiles } from '@/js/uploader'
// other
import { ref } from 'vue'
import { MdEditor } from 'md-editor-v3'
import 'md-editor-v3/lib/style.css'
const { v4: uuidv4 } = require('uuid')
import YAML from 'yaml'


function createPostFilename(name = 'post', extension = 'markdown', date = null){
    if (date === null){
        date = convertDateToPostFilename(new Date())
    }
    // check if a name has been passed
    if (name.length > 1) {
        // remove spaces
        name = spacesToHyphens(name)
        // remove special characters
        name = removeSpecialChars(name)
        // test if name starts with a valid character
        name = makeIndexZeroALetter(name)
        // test for if name is too long
        name = stringLenUnderFifty(name)
    } else {
        name = 'post'
    }

    return date + '-' + name + '.' + extension
}

function spacesToHyphens(string) {
    return string.replace(/ /g, '-')
}

function removeSpecialChars(string) {
    var specials = /[!@#£$%^&~*()_+\=\[\]{};':"\\|,.<>\/?]+/
        return string.replace(new RegExp(specials, 'g'), '')
}

function makeIndexZeroALetter(string) {
    const validCharacters = /^[a-z]+$/i
    if (string[0].match(validCharacters)) {
        return string
    } else {
        return 'post-' + string
    }
}

function stringLenUnderFifty(string) {
    if (string.length >= 50) {
       return string.slice(0, 51)
    } else {
        return string
    }
}

function convertDateToPostFilename(date) {
    return date.toISOString().substr(0, 10)
}

export default {
    name: 'PostEditor',
    components: {
        MdEditor,
        MdFields,
        Gallery,
        ModalSimple,
        SaveButtons,
        OpenButtons,
        LoadingSpinner,
        NotificationMessage,
    },
    data() {
        return {
            starterText: '# Heading here...\n\nText here...',
            text: '# Heading here...\n\nText here...',
            openedFrontMatter: '',
            fields: {},
            saveFields: {},
            activeTab: 0,
            primaryImageSelected: false,
            primaryImageFilename: '',
            refreshImages: false,
            showSavedMsg: false,
            saveModalActive: false,
            openModalActive: false,
            loading: false,
            postId: ''
        }
    },
    methods: {
        onSave() {
            this.saveModalActive = true
        },
        async putOnS3() {
            this.fields.published = this.saveFields.published

            await putTextFile(
                this.postText,
                this.postId, //uuid is used instead of filename, which is now passed as a tag
                'text/plain',
                this.tags,
                `${process.env.VUE_APP_BUCKET_BLOG_PREFIX}/posts/`,
                process.env.VUE_APP_BUCKET_BLOG,
                false,
                true
            )

            this.showSavedMsg = true
        },
        savePostLocally() {
            this.fields.published = this.saveFields.published
            saveTextFileLocally(
                this.postText,
                removeFileExtension(this.postFilename),
                'txt',
                'text/plain'
            )
        },
        onOpen() {
            this.openModalActive = true
        },
        async saveFieldsEvent(event) {
            if (event.saveFields.destination.length > 0) {
                this.saveFields = event.saveFields
                this.fields.date = this.fields.date + ' 12:00:00 +0000'
                this.saveModalActive = false
                this.loading = true
    
                if (this.saveFields.destination === 'Locally') {
                    await this.savePostLocally()
                    this.loading = false
                } else if (this.saveFields.destination === 'S3') {
                    await this.putOnS3()
                    this.loading = false
                } else {
                    console.log('save destination not supported')
                }
            } else {
                console.log('please select a destination')
            }
        },
        async onUploadImg(files) {
            try {
                await addFiles(
                    files, 
                    {}, 
                    `${process.env.VUE_APP_BUCKET_BLOG_PREFIX}/imgs/`, 
                    process.env.VUE_APP_BUCKET_BLOG,
                    false
                )
                console.log('image(s) uploaded')
                this.refreshImages = true
            } catch(error) {
                console.error(`error: ${error}`)
            }
        },
        mdFields(event) {
            this.fields = Object.assign({}, 
                {layout: "post"}, 
                {title: event.mdFields.title}, 
                {subtitle: event.mdFields.subtitle}, 
                {date: event.mdFields.date}, 
                {author: event.mdFields.author}, 
                {categories: event.mdFields.category}, 
                {image: this.fields.image}, 
                {'image-caption': event.mdFields.caption}, 
                {excerpt: event.mdFields.excerpt}, 
                {published: false})
        },
        onImageObject(imageObject) {
            const imageHtml = `<div class="post-text-image-container">\n    <figure>\n       <img src="${imageObject.url}" class="post-text-image-center">\n        <figcaption class="italic-caption"></figcaption>\n    </figure>\n</div>`
            this.text = this.text + '\n\n' + imageHtml
            this.activeTab = 0
        },
        onPrimaryImageObject(imageObject) {
            this.fields.image = imageObject.url
            this.primaryImageFilename = imageObject.filename
            this.activeTab = 0
            this.primaryImageSelected = true
        },
        onChooseImage() {
            this.activeTab = 1
        },
        onPostOpened(event) {
            const post = event.post
            this.text = post.body
            this.fields = post.frontMatter
            this.primaryImageFilename = this.fields.image
            this.primaryImageSelected = true
            this.openModalActive = false
            this.postId = post.postId
        }
    },
    mounted(){
        if (this.postId.length === 0){
            this.postId = uuidv4()
        }
    },
    computed: {
        postFilename(){
            return createPostFilename(
                this.fields.title,
                'markdown',
                this.fields.date
            )
        },
        postText(){
            return this.frontMatter + '\n\n' + this.text
        },
        frontMatter(){
            return '---\n' + YAML.stringify(this.fields) + '---'
        },
        tags(){
            return Object.assign(
                {}, 
                {postTitle: this.fields.title}, 
                {postDate: this.fields.date}, 
                {postAuthor: this.fields.author}, 
            )
        }
    }
}
</script>

